// Money and discount maths shared by everything that shows a subscription
// price. Amounts are minor units (bani; 29900 = 299 RON) throughout.

import {
  InvitationTrialTerms,
  SubscriptionDiscountType,
} from "@/server-side/types/subscription";

// The published price of the STANDARD plan, mirroring the backend's
// `subscription.standard-price-minor` / `subscription.currency`. There is no
// public endpoint to read it from (every /subscriptions route is authenticated),
// so the marketing page and the admin form both take it from here. Keep it in
// step with the backend's application.yaml if the price ever changes.
export const STANDARD_PRICE_MINOR = 29900;
export const STANDARD_PRICE_CURRENCY = "RON";

// Standard domestic VAT rate, mirroring `subscription.display-vat-rate`. For
// display only: the amount actually charged is computed by the tax gateway per
// charge and may differ (reverse charge, a buyer outside Romania).
export const DISPLAY_VAT_RATE = 21;

export const minorToMajor = (minor: number | null | undefined): number =>
  (minor ?? 0) / 100;

// Gross-up a NET minor amount by a VAT percentage (e.g. 21) for a VAT-inclusive
// display figure. The server sends grossPriceMinor for the effective price; use
// this only where the server exposes just a net amount (e.g. the pre-discount
// base). Mirrors the backend's half-up rounding.
export const withVatMinor = (
  netMinor: number | null | undefined,
  vatRate: number | null | undefined
): number => {
  const net = netMinor ?? 0;
  if (!vatRate) return net;
  return net + Math.round((net * vatRate) / 100);
};

// Format minor units as a localized amount with currency, e.g. "299 RON".
export const formatMoney = (
  minor: number | null | undefined,
  currency: string | null | undefined,
  locale = "ro-RO"
): string => {
  const amount = minorToMajor(minor).toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return currency ? `${amount} ${currency}` : amount;
};

// What a subscription on these terms actually costs per month.
//
// A live subscription carries `effectivePriceMinor` computed server-side, but
// invitation terms don't — they're only a promise of what the price will be —
// so the same rule is applied here for display. Keep it in step with the
// backend if the discount rules ever change.
export const discountedPriceMinor = (
  basePriceMinor: number,
  discountType: SubscriptionDiscountType | null | undefined,
  discountValue: number | null | undefined
): number => {
  const base = basePriceMinor ?? 0;
  const value = discountValue ?? 0;
  if (!discountType || discountType === "NONE" || !value) return base;
  const discounted =
    discountType === "PERCENT"
      ? Math.round(base * (1 - value / 100))
      : base - value;
  // A discount can be configured larger than the price; never quote a negative.
  return Math.max(0, discounted);
};

export const termsPriceMinor = (terms: InvitationTrialTerms): number =>
  discountedPriceMinor(
    terms.basePriceMinor,
    terms.discountType,
    terms.discountValue
  );

export const termsHasDiscount = (terms: InvitationTrialTerms): boolean =>
  terms.discountType !== "NONE" && !!terms.discountValue;
